import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { serverHost, createEndpoints } from "variables/serverdata";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";

const IVRAddEdit = () => {
  const dialplans = [
    { value: "transfer:*98 XML 10.0.190.48", label: "Check Voicemail" },
    { value: "transfer:*411 XML 10.0.190.48", label: "Company Directory" },
    { value: "hangup:", label: "Hangup" },
    { value: "transfer:*732 XML 10.0.190.48", label: "Record" },
  ];
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [showAdvanced, setShowAdvanced] = useState(false);
  const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
  const site_uuid = currentSite?.domain_uuid || null;
  const [formData, setFormData] = useState({
    // Basic fields
    ivr_menu_name: null,
    ivr_menu_extension: null,
    ivr_menu_parent_uuid: null,
    language: null,
    ivr_menu_greet_long: null,
    ivr_menu_greet_short: null,
    ivr_menu_invalid_sound: null,
    ivr_menu_exit_sound: null,
    ivr_menu_pin_number: null,
    ivr_menu_confirm_macro: null,
    ivr_menu_confirm_key: null,
    ivr_menu_tts_engine: "flite",
    ivr_menu_tts_voice: "rms",
    ivr_menu_confirm_attempts: "1",
    ivr_menu_timeout: "3000",
    exit_action: null,
    ivr_menu_direct_dial: false,
    ivr_menu_ringback: null,
    ivr_menu_cid_prefix: null,
    ivr_menu_context: "",
    ivr_menu_enabled: true,
    ivr_menu_description: null,
    // Advanced fields
    ivr_menu_inter_digit_timeout: "2000",
    ivr_menu_max_failures: "1",
    ivr_menu_max_timeouts: "1",
    ivr_menu_digit_len: "5",
  });
  // Options state - manage up to 5 options
  const [options, setOptions] = useState([
    {
      option: null,
      destination: null,
      order: 0,
      description: null,
      enabled: false,
    },
  ]);
  const [greetOptions, setGreetOptions] = useState([
    { value: "say:", name: "say" },
    { value: "tone_stream:", name: "tone_stream" },
  ]);

  const [soundsList, setSoundsList] = useState([]);

  const [languageList, setLanguageOptions] = useState([
    { value: "en/us/callie", name: "en-us callie" },
  ]);
  const [recordingsList, setRecordingsList] = useState([]);
  const [extensionList, setExtensionList] = useState([]);
  const [bridgeList, setBridgeList] = useState([]);
  const [voicemailList, setVoicemailList] = useState([]);
  const [tonesList, setTonesList] = useState([]);
  const [flowsList, setFlowsList] = useState([]);
  const [ivrList, setIVRList] = useState([]);
  const ivr_other = [
    { value: "menu-top:", name: "top" },
    { value: "menu-back:", name: "back" },
    { value: "menu-exit:", name: "exit" },
  ];
  const [timeConditionsList, setTCList] = useState([]);
  const [destinationsList, setDestList] = useState([]);
  const [ringGroupList, setRingGroup] = useState([]);
  const [domainList, setDomainList] = useState([]);
  const [ringtonesList, setRingtonesList] = useState([]);
  const [loading, setLoading] = useState([]);
  const endpoints = useMemo(
    () =>
      createEndpoints({
        setExtensionList,
        setBridgeList,
        setVoicemailList,
        setFlowsList,
        setIVRList,
        setTCList,
        setDestList,
        setRingGroup,
        setDomainList,
        site_uuid,
      }),
    [],
  );

  useEffect(() => {
    const fetchRecordings = async (e) => {
      try {
        const domain_uuid = currentSite?.site_uuid || null;
        const response = await fetch(
          `${serverHost}/get_recordings/${domain_uuid}`,
        );
        if (!response.ok) throw new Error(`Failed to fetch domain context`);
        const data = await response.json();
        console.log(data);
        setRecordingsList(data.results || []);
      } catch (err) {
        setError(err.message);
      }
    };
    const fetchDomain = async (e) => {
      try {
        const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
        const domain_uuid = currentSite?.domain_uuid || null;
        const response = await fetch(
          `${serverHost}/fetch_domain_name/${domain_uuid}`,
        );
        if (!response.ok) throw new Error(`Failed to fetch domain context`);
        const data = await response.json();
        console.log("Fetched domain: ", data.result)
        setFormData((prev) => ({ ...prev, ivr_menu_context: data.result }));
      } catch (err) {
        setError(err.message);
      }
    };

    const fetchRingtones = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_rows?table=v_vars&pageSize=50&page=1&cfilter=var_category&vfilter=Ringtones`,
        );
        if (res.ok) {
          const data = await res.json();
          console.log(data);
          const tones = data.results.map((r) => ({
            value: `\${${r.var_name}}`,
            name: r.var_name,
          }));
          console.log(tones);
          setRingtonesList(tones);
        }
      } catch (err) {
        console.error("Failed to fetch extensions:", err);
      }
    };
    const fetchTones = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_rows?table=v_vars&pageSize=50&page=1&cfilter=var_category&vfilter=Tones`,
        );
        if (res.ok) {
          const data = await res.json();
          console.log(data);
          const tones = data.results.map((r) => ({
            value: `playback:tone_stream://${r.var_value}`,
            name: r.var_name,
          }));
          console.log(tones);
          setTonesList(tones);
        }
      } catch (err) {
        console.error("Failed to fetch extensions:", err);
      }
    };

    const fetchData = async () => {
      for (const key in endpoints) {
        const { url, map, setter } = endpoints[key];
        try {
          const res = await fetch(url);
          if (!res.ok) continue;

          const data = await res.json();
          const mapped = data.results.map(map);
          console.log(key, mapped);

          setter(mapped);
        } catch (err) {
          console.error(`Failed to fetch ${key}:`, err);
        }
      }
    };
    const fetchIVR = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_row?table=v_ivr_menus&pk=ivr_menu_uuid&id=${encodeURIComponent(
            id,
          )}`,
        );
        if (!res.ok)
          throw new Error(
            res.status === 404 ? "Row not found" : "Failed to fetch row",
          );
        const data = await res.json();
        if (!data?.result) throw new Error("Row not found");
        const ivr = data.result;
        setFormData({
          ivr_menu_name: ivr.ivr_menu_name,
          ivr_menu_extension: ivr.ivr_menu_extension,
          ivr_menu_parent_uuid: ivr.ivr_menu_parent_uuid,
          language: `${ivr.ivr_menu_language}/${ivr.ivr_menu_dialect}/${ivr.ivr_menu_voice}`,
          ivr_menu_greet_long: ivr.ivr_menu_greet_long,
          ivr_menu_greet_short: ivr.ivr_menu_greet_short,
          ivr_menu_invalid_sound: ivr.ivr_menu_invalid_sound,
          ivr_menu_exit_sound: ivr.ivr_menu_exit_sound,
          ivr_menu_pin_number: ivr.ivr_menu_pin_number,
          ivr_menu_confirm_macro: ivr.ivr_menu_confirm_macro,
          ivr_menu_confirm_key: ivr.ivr_menu_confirm_key,
          ivr_menu_tts_engine: ivr.ivr_menu_tts_engine,
          ivr_menu_tts_voice: ivr.ivr_menu_tts_voice,
          ivr_menu_confirm_attempts: ivr.ivr_menu_confirm_attempts,
          ivr_menu_timeout: ivr.ivr_menu_timeout,
          exit_action: `${ivr.ivr_menu_exit_app}:${ivr.ivr_menu_exit_data}`,
          ivr_menu_direct_dial: ivr.ivr_menu_direct_dial,
          ivr_menu_ringback: ivr.ivr_menu_ringback,
          ivr_menu_cid_prefix: ivr.ivr_menu_cid_prefix,
          ivr_menu_context: ivr.ivr_menu_context,
          ivr_menu_enabled: ivr.ivr_menu_enabled,
          ivr_menu_description: ivr.ivr_menu_description,
          ivr_menu_inter_digit_timeout: ivr.ivr_menu_inter_digit_timeout,
          ivr_menu_max_failures: ivr.ivr_menu_max_failures,
          ivr_menu_max_timeouts: ivr.ivr_menu_max_timeouts,
          ivr_menu_digit_len: ivr.ivr_menu_digit_len,
        });
      } catch (err) {
        console.error("Error fetching ivr data, ", err);
        setError("Failed to fetch data");
      }
    };
    const fetchOptions = async () => {
      try {
        const res = await fetch(
          `${serverHost}/fetch_options/${encodeURIComponent(id)}`,
        );
        if (!res.ok)
          throw new Error(
            res.status === 404 ? "Row not found" : "Failed to fetch row",
          );
        const data = await res.json();
        if (!data?.result) throw new Error("Row not found");
        const opt = data.result;
        const opt_list = opt.map((o) => ({
          option_uuid: o.ivr_menu_option_uuid,
          option: o.ivr_menu_option_digits,
          destination: `${o.ivr_menu_option_action}:${o.ivr_menu_option_param}`,
          order: o.ivr_menu_option_order,
          description: o.ivr_menu_option_description,
          enabled: o.ivr_menu_option_enabled,
        }));
        console.log(opt_list);
        setOptions(opt_list);
      } catch (err) {
        console.error("Error fetching ivr data, ", err);
        setError("Failed to fetch data");
      }
    };

    const fetchSounds = async () => {
      try {
        const res = await fetch(`${serverHost}/get_sounds`);
        if (res.ok) {
          const data = await res.json();
          console.log(data);
          const sounds = data.result;
          console.log("sounds: ", sounds);
          setSoundsList(sounds);
        }
      } catch (err) {
        console.error("Failed to fetch sounds:", err);
      }
    };
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchData(),
          fetchTones(),
          fetchRingtones(),
          fetchSounds(),
          fetchRecordings(),
          ...(id ? [fetchOptions(), fetchIVR()] : [fetchDomain()]),
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = field === "enabled" ? value : value;
    setOptions(newOptions);
  };

  const addOption = () => {
    if (options.length < 5) {
      setOptions([
        ...options,
        {
          option: "",
          destination: "",
          order: 0,
          description: "",
          enabled: false,
        },
      ]);
    }
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    // Reorder remaining options
    newOptions.forEach((opt, i) => (opt.order = i + 1));
    setOptions(newOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // Validation
      if (
        !formData.ivr_menu_name ||
        !formData.ivr_menu_extension ||
        !formData.ivr_menu_timeout
      ) {
        setError("Please fill in all required fields");
        return;
      }
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const payload = {
        data: {
          ...formData,
        },
        options: options,
        client_uuid: site.client_uuid,
        site_name: site.company_name,
        insert_uuid: JSON.parse(localStorage.getItem("user")).user_uuid,
      };

      let url = `${serverHost}/ivr_menu/${site.site_uuid}`;
      if (id) {
        url = `${serverHost}/update_ivr/${id}`;
        payload.site_uuid = site.site_uuid;
      }
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Show success message
        navigate("/admin/hucs/ivr-menus"); // Navigate back to call flows list
      } else {
        const error = await response.json();
        console.error("Failed to create call flow:", error);
        // Show error message
      }
    } catch (error) {
      console.error("Error creating call flow:", error);
      // Show error message
    }
  };

  const handleCancel = () => {
    navigate("/admin/hucs/ivr-menus"); // Go back to previous page
  };

  const inputStyle = {
    backgroundColor: isDark ? "#374151" : "#fff",
    color: isDark ? "#e2e8f0" : "#495057",
    borderColor: isDark ? "#4b5563" : "#ced4da",
  };

  const labelStyle = {
    color: isDark ? "#e2e8f0" : "#495057",
    fontWeight: 500,
  };
  if (loading) {
      return (
        <>
          <SimpleHeader name="Edit Inbound Route" parentName="HUCS" />
          <Container className="mt--6">
            <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
              <Card.Body className="p-4 text-center">
                <p>Loading IVR<span className="loading-dots"></span></p>
              </Card.Body>
            </Card>
          </Container>
        </>
      );
    }
  return (
    <>
      <SimpleHeader
        name={`${id ? "EDIT" : "ADD"} IVR Menu`}
        parentName="HUCS"
      />
      <Container fluid className="mt--6">
        <Card className="shadow-sm" style={{ border: "none" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              {`${id ? "EDIT" : "ADD"}`} IVR MENU
            </h5>
          </Card.Header>

          <Card.Body
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              padding: "1.5rem",
            }}
          >
            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Name <span style={{ color: "#dc3545" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="ivr_menu_name"
                      value={formData.ivr_menu_name}
                      onChange={handleChange}
                      placeholder="Enter IVR menu name"
                      required
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Extension <span style={{ color: "#dc3545" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="ivr_menu_extension"
                      value={formData.ivr_menu_extension}
                      onChange={handleChange}
                      placeholder="Enter extension number"
                      required
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Parent Menu</Form.Label>
                    <Input
                      type="select"
                      name="ivr_menu_parent_uuid"
                      value={formData.ivr_menu_parent_uuid}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      {ivrList.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Language</Form.Label>
                    <Input
                      type="select"
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      {languageList.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Greet Long</Form.Label>
                    <Input
                      type="select"
                      name="ivr_menu_greet_long"
                      value={formData.ivr_menu_greet_long}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      {greetOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                      <optgroup label="Recordings">
                        {recordingsList.map((opt) => (
                          <option
                            key={opt.recording_filename}
                            value={opt.recording_filename}
                          >
                            {opt.recording_name}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Greet Short</Form.Label>
                    <Input
                      type="select"
                      name="ivr_menu_greet_short"
                      value={formData.ivr_menu_greet_short}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      {greetOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                      <optgroup label="Recordings">
                        {recordingsList.map((opt) => (
                          <option
                            key={opt.recording_filename}
                            value={opt.recording_filename}
                          >
                            {opt.recording_name}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Timeout <span style={{ color: "#dc3545" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="ivr_menu_timeout"
                      value={formData.ivr_menu_timeout}
                      onChange={handleChange}
                      placeholder="3000"
                      required
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Exit Action</Form.Label>
                    <Input
                      type="select"
                      name="exit_action"
                      value={formData.exit_action}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      <optgroup label="Bridges">
                        {bridgeList.map((br) => (
                          <option
                            key={br.bridge_uuid}
                            value={`bridge:${br.destination}`}
                          >
                            {br.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Call Flows">
                        {flowsList.map((cf) => (
                          <option key={cf.name} value={`transfer:${cf.value}`}>
                            {cf.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Destinations">
                        {destinationsList.map((d) => (
                          <option value={`transfer:${d.value}`}>
                            {d.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Extensions">
                        {extensionList.map((ext) => (
                          <option
                            key={ext.extension_uuid}
                            value={`transfer:${ext.name} XML ${ext.accountcode}`}
                          >
                            {ext.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="IVR Menus">
                        {ivrList.map((ivr) => (
                          <option
                            key={ivr.name}
                            value={`transfer:${ivr.value}`}
                          >
                            {ivr.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Ring Groups">
                        {ringGroupList.map((rg) => (
                          <option value={`transfer:${rg.value}`}>
                            {rg.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Time Conditions">
                        {timeConditionsList.map((tc) => (
                          <option key={tc.name} value={`transfer:${tc.value}`}>
                            {tc.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Tones">
                        {tonesList.map((tone) => (
                          <option
                            key={tone.name}
                            value={`playback:tone_stream://${tone.value}`}
                          >
                            {tone.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Voicemails">
                        {voicemailList.map((vm) => (
                          <option
                            key={vm.voicemail_uuid}
                            value={`transfer:${vm.value}`}
                          >
                            {vm.voicemail_id}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Dialplans">
                        {dialplans.map((dp) => (
                          <option key={dp.value} value={dp.value}>
                            {dp.label}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              {/* OPTIONS SECTION */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label style={{ ...labelStyle, marginBottom: 0 }}>
                    Options
                  </Form.Label>
                  {options.length < 5 && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addOption}
                      style={{
                        borderColor: "#5469d4",
                        color: "#5469d4",
                      }}
                    >
                      + Add Option
                    </Button>
                  )}
                </div>

                {options.map((opt, index) => (
                  <Card
                    key={index}
                    className="mb-2"
                    style={{
                      backgroundColor: isDark ? "#374151" : "#f8f9fa",
                      border: `1px solid ${isDark ? "#4b5563" : "#dee2e6"}`,
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small
                          style={{
                            color: isDark ? "#9ca3af" : "#6c757d",
                            fontWeight: 600,
                          }}
                        >
                          Option {index + 1}
                        </small>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => removeOption(index)}
                        >
                          Remove
                        </Button>
                      </div>
                      <Row>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Option
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={opt.option}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "option",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g., 1"
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Destination
                            </Form.Label>
                            <Input
                              type="select"
                              size="sm"
                              value={opt.destination}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "destination",
                                  e.target.value,
                                )
                              }
                              style={inputStyle}
                            >
                              <option value="">Select...</option>
                              <optgroup label="Bridges">
                                {bridgeList.map((br) => (
                                  <option
                                    key={br.bridge_uuid}
                                    value={`menu-exec-app:bridge ${br.destination}`}
                                  >
                                    {br.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Call Flows">
                                {flowsList.map((cf) => (
                                  <option
                                    key={cf.name}
                                    value={`menu-exec-app:transfer ${cf.value}`}
                                  >
                                    {cf.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Destinations">
                                {destinationsList.map((d) => (
                                  <option
                                    value={`menu-exec-app:transfer ${d.value}`}
                                  >
                                    {d.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Extensions">
                                {extensionList.map((ext) => (
                                  <option
                                    key={ext.extension_uuid}
                                    value={`menu-exec-app:transfer ${ext.name} XML ${ext.accountcode}`}
                                  >
                                    {ext.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="IVR Menus">
                                {ivrList.map((ivr) => (
                                  <option
                                    key={ivr.name}
                                    value={`menu-exec-app:transfer ${ivr.transfer}`}
                                  >
                                    {ivr.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="IVR Menus - Others">
                                {ivr_other.map((ivr) => (
                                  <option key={ivr.name} value={ivr.value}>
                                    {ivr.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="IVR Menus - Sub">
                                {ivrList.map((ivr) => (
                                  <option
                                    key={ivr.name}
                                    value={`menu-sub:${ivr.value}`}
                                  >
                                    {ivr.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Ring Groups">
                                {ringGroupList.map((rg) => (
                                  <option
                                    value={`menu-exec-app:transfer ${rg.value}`}
                                  >
                                    {rg.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Time Conditions">
                                {timeConditionsList.map((tc) => (
                                  <option
                                    key={tc.name}
                                    value={`menu-exec-app:transfer ${tc.value}`}
                                  >
                                    {tc.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Tones">
                                {tonesList.map((tone) => (
                                  <option
                                    key={tone.name}
                                    value={`menu-exec-app:playback tone_stream://${tone.value}`}
                                  >
                                    {tone.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Voicemails">
                                {voicemailList.map((vm) => (
                                  <option
                                    key={vm.voicemail_uuid}
                                    value={`menu-exec-app:transfer ${vm.value}`}
                                  >
                                    {vm.voicemail_id}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Dialplans">
                                {dialplans.map((dp) => (
                                  <option
                                    key={dp.value}
                                    value={`menu-exec-app:${dp.value}`}
                                  >
                                    {dp.label}
                                  </option>
                                ))}
                              </optgroup>
                            </Input>
                          </Form.Group>
                        </Col>
                        <Col md={1}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Order
                            </Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={opt.order}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "order",
                                  e.target.value,
                                )
                              }
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Description
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={opt.description}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                              placeholder="Description"
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                          <Form.Group>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                marginBottom: "1.5rem",
                              }}
                            >
                              <span
                                style={{
                                  color: isDark ? "#e2e8f0" : "#495057",
                                }}
                              >
                                Enabled
                              </span>

                              <label className="custom-toggle mb-0">
                                <input
                                  type="checkbox"
                                  id="enabled-checkbox"
                                  name="enabled"
                                  checked={opt.enabled}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      index,
                                      "enabled",
                                      e.target.checked,
                                    )
                                  }
                                />
                                <span
                                  className="custom-toggle-slider rounded-circle"
                                  data-label-off="Off"
                                  data-label-on="On"
                                />
                              </label>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Ringback</Form.Label>
                    <Input
                      type="select"
                      name="ivr_menu_ringback"
                      value={formData.ivr_menu_ringback}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      <optgroup label="Music On Hold">
                        <option
                          value="local_stream://default"
                          selected="selected"
                        >
                          default
                        </option>
                      </optgroup>
                      <optgroup label="Recordings">
                        {recordingsList.map((opt) => (
                          <option
                            key={opt.recording_filename}
                            value={opt.recording_filename}
                          >
                            {opt.recording_name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Ringtones">
                        {ringtonesList.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Tones">
                        {tonesList.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.name}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Caller ID Name Prefix
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="ivr_menu_cid_prefix"
                      value={formData.ivr_menu_cid_prefix}
                      onChange={handleChange}
                      placeholder="Enter prefix"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Context</Form.Label>
                    <Form.Control
                      type="text"
                      name="ivr_menu_context"
                      value={formData.ivr_menu_context}
                      onChange={handleChange}
                      placeholder="Enter context"
                      style={{
                        backgroundColor: isDark ? "#000000ff" : "#beb6b6ff",
                        color: isDark ? "#e2e8f0" : "#495057",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                      }}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                      Direct Dial
                    </span>

                    <label className="custom-toggle mb-0">
                      <input
                        type="checkbox"
                        id="direct-dial-checkbox"
                        name="ivr_menu_direct_dial"
                        checked={formData.ivr_menu_direct_dial}
                        onChange={handleChange}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="Off"
                        data-label-on="On"
                      />
                    </label>
                  </div>
                </Col>

                <Col md={6}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                      Enabled
                    </span>

                    <label className="custom-toggle mb-0">
                      <input
                        type="checkbox"
                        id="enabled"
                        name="enabled"
                        checked={formData.ivr_menu_enabled}
                        onChange={handleChange}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="Off"
                        data-label-on="On"
                      />
                    </label>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="ivr_menu_description"
                      value={formData.ivr_menu_description}
                      onChange={handleChange}
                      placeholder="Enter description"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ADVANCED SECTION */}
              <div className="mb-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-100 d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: isDark ? "#374151" : "#fff",
                    color: isDark ? "#e2e8f0" : "#495057",
                    borderColor: isDark ? "#4b5563" : "#ced4da",
                    padding: "0.75rem 1rem",
                    fontWeight: 600,
                  }}
                >
                  <span>ADVANCED</span>
                  <span>{showAdvanced ? "−" : "+"}</span>
                </Button>

                {showAdvanced && (
                  <div
                    className="mt-3 p-3"
                    style={{
                      backgroundColor: isDark ? "#374151" : "#f8f9fa",
                      border: `1px solid ${isDark ? "#4b5563" : "#dee2e6"}`,
                      borderRadius: "0.25rem",
                    }}
                  >
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>
                            Invalid Sound
                          </Form.Label>
                          <Input
                            type="select"
                            name="ivr_menu_invalid_sound"
                            value={formData.ivr_menu_invalid_sound}
                            onChange={handleChange}
                            style={inputStyle}
                          >
                            <option value="">Select...</option>
                            {soundsList.map((opt) => (
                              <option key={opt.filename} value={opt.filename}>
                                {opt.filename}
                              </option>
                            ))}
                          </Input>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>Exit Sound</Form.Label>
                          <Input
                            type="select"
                            name="ivr_menu_exit_sound"
                            value={formData.ivr_menu_exit_sound}
                            onChange={handleChange}
                            style={inputStyle}
                          >
                            <option value="">Select...</option>
                            {soundsList.map((opt) => (
                              <option key={opt.filename} value={opt.filename}>
                                {opt.filename}
                              </option>
                            ))}
                          </Input>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>PIN</Form.Label>
                          <Form.Control
                            type="text"
                            name="ivr_menu_pin_number"
                            value={formData.ivr_menu_pin_number}
                            onChange={handleChange}
                            placeholder="Enter PIN"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>
                            Confirm Macro
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="ivr_menu_confirm_macro"
                            value={formData.ivr_menu_confirm_macro}
                            onChange={handleChange}
                            placeholder="Enter confirm macro"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>
                            Confirm Key
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="ivr_menu_confirm_key"
                            value={formData.ivr_menu_confirm_key}
                            onChange={handleChange}
                            placeholder="Enter confirm key"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>TTS Engine</Form.Label>
                          <Form.Control
                            type="text"
                            name="ivr_menu_tts_engine"
                            value={formData.ivr_menu_tts_engine}
                            onChange={handleChange}
                            placeholder="flite"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={labelStyle}>TTS Voice</Form.Label>
                          <Form.Control
                            type="text"
                            name="ivr_menu_tts_voice"
                            value={formData.ivr_menu_tts_voice}
                            onChange={handleChange}
                            placeholder="rms"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label
                            style={{ ...labelStyle, fontWeight: "bolder" }}
                          >
                            Confirm Attempts{" "}
                            <span style={{ color: "#dc3545" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="ivr_menu_confirm_attempts"
                            value={formData.ivr_menu_confirm_attempts}
                            onChange={handleChange}
                            placeholder="1"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label
                            style={{ ...labelStyle, fontWeight: "bolder" }}
                          >
                            Inter-Digit Timeout{" "}
                            <span style={{ color: "#dc3545" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="ivr_menu_inter_digit_timeout"
                            value={formData.ivr_menu_inter_digit_timeout}
                            onChange={handleChange}
                            placeholder="2000"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label
                            style={{ ...labelStyle, fontWeight: "bolder" }}
                          >
                            Max Failures{" "}
                            <span style={{ color: "#dc3545" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="ivr_menu_max_failures"
                            value={formData.ivr_menu_max_failures}
                            onChange={handleChange}
                            placeholder="1"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label
                            style={{ ...labelStyle, fontWeight: "bolder" }}
                          >
                            Max Timeouts{" "}
                            <span style={{ color: "#dc3545" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="ivr_menu_max_timeouts"
                            value={formData.ivr_menu_max_timeouts}
                            onChange={handleChange}
                            placeholder="1"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label
                            style={{ ...labelStyle, fontWeight: "bolder" }}
                          >
                            Digit Length{" "}
                            <span style={{ color: "#dc3545" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="ivr_menu_digit_len"
                            value={formData.ivr_menu_digit_len}
                            onChange={handleChange}
                            placeholder="5"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between mt-4">
                <Button variant="danger" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSubmit}>
                  {`${id ? "EDIT" : "ADD"}`} IVR Menu
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default IVRAddEdit;
